import React, { Component } from 'react';
import {
    Button,
    Dropdown,
    InputGroup,
    FormControl,
    Table,
    Badge,
    OverlayTrigger,
    Tooltip
} from 'react-bootstrap';
import SearchSharpIcon from '@material-ui/icons/SearchSharp';
import MoreVertSharpIcon from '@material-ui/icons/MoreVertSharp';
import { getDateWithoutTimeInDdMmYyyy, debounce } from '../../function';
import Loader from '../../component/loader/loader';
import CreateReportModal from '../../component/create-report';
import axios from '../../axios';
import apiError from '../../error';
import './report.css';
import { PiFileTextBold, PiPlus, PiSparkleBold, PiTrash } from 'react-icons/pi';
import Pagination from 'react-js-pagination';
import { toast } from 'react-toastify';
import swal from 'sweetalert';

export default class Report extends Component {
    constructor(props) {
        super(props);
        this.state = {
            reports: [],
            showCreateReportModal: false,
            activePage: 1,
            limit: 15,
            count: 0,
            search: '',
            loading: true,
            showLimitInLabel: false,
            selectedType: '',
            selectedModule: '',
            modules: [],
            modulesLoaded: false,
            permissibleActionsIAM: {},
            selectedReportIds: [],
        };
        this.selectRef = React.createRef();
    }

    componentDidMount() {
        this.fetchAllReports();
        this.fetchModules();
        this.loadPermissibleActions();
    }

    fetchAllReports = () => {
        const { activePage, limit, search, selectedType, selectedModule } = this.state;
        
        axios.get(`/api/custom-Reports`, {
            params: {
                page: activePage,
                limit: limit,
                search: search,
                type: selectedType,
                module: selectedModule,
            }
        })
            .then(res => {
                const reports = Array.isArray(res.data.customReports) ? res.data.customReports : [];
                const selectableIds = reports
                    .filter((report) => !this.isDefaultReport(report) && report.id)
                    .map((report) => report.id);
                this.setState({
                    loading: false,
                    reports,
                    count: res.data.totalCount,
                    selectedReportIds: this.state.selectedReportIds.filter((id) => selectableIds.includes(id)),
                });
            })
            .catch(err => {
                apiError(err);
                this.setState({ loading: false });
            });
    };

    debouncedFetchAllReports = debounce(() => this.fetchAllReports(), 500);

    formatModuleLabel = (module = '') => {
        return String(module || '')
            .replace(/-/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/\b\w/g, (c) => c.toUpperCase())
            .trim();
    }

    handleReportEdit = (id) => {
        this.props.history.push(`/reports/report/${id}`);
    }

    isDefaultReport = (report = {}) => {
        const type = String(report.type || report.reportType || '').toLowerCase();
        return type === 'default' || report.isDefault === true;
    }

    getDefaultReportName = (report = {}) => {
        return report.reportName || report.slug || report.name?.toLowerCase().trim().replace(/\s+/g, '-') || '';
    }

    getAvailableModules = (report = {}) => {
        if (Array.isArray(report.availableModules) && report.availableModules.length) {
            return report.availableModules;
        }
        return ['user', 'designation', 'department', 'location'];
    }

    /**
     * Label for the Module column. Never default to "user" when the API omits `report.module`
     * (that misled users when filtering by department / designation / location).
     */
    getReportModuleLabel = (report = {}) => {
        if (report.module) {
            return this.formatModuleLabel(report.module);
        }
        const { selectedModule } = this.state;
        if (selectedModule) {
            return this.formatModuleLabel(selectedModule);
        }
        const available = this.getAvailableModules(report);
        if (!available.length) {
            return '—';
        }
        if (available.length === 1) {
            return this.formatModuleLabel(available[0]);
        }
        return 'All modules';
    }

    handleReportOpen = (report) => {
        if (this.isDefaultReport(report)) {
            const reportName = this.getDefaultReportName(report);
            this.props.history.push({
                pathname: `/reports/default/${reportName}`,
                state: {
                    reportTitle: report.name,
                    availableModules: this.getAvailableModules(report),
                }
            });
            return;
        }

        this.handleReportEdit(report.id);
    }

    handleReportDelete = (id) => {
        swal({
            title: "Are you sure you want to delete this report?",
            icon: "warning",
            buttons: ["No", "Yes"],
            dangerMode: true,
        }).then(willDelete => {
            if (willDelete) {
                axios.delete(`/api/custom-Reports/${id}`)
                    .then(res => {
                        toast.success(res.data.message);
                        this.setState((prevState) => ({
                            selectedReportIds: prevState.selectedReportIds.filter((reportId) => reportId !== id),
                        }));
                        this.fetchAllReports();
                    })
                    .catch(err => apiError(err));
            }
        });
    }

    getSelectableReportIds = () => {
        return this.state.reports
            .filter((report) => !this.isDefaultReport(report) && report.id)
            .map((report) => report.id);
    }

    areAllSelectableReportsSelected = () => {
        const selectableIds = this.getSelectableReportIds();
        if (!selectableIds.length) {
            return false;
        }
        return selectableIds.every((id) => this.state.selectedReportIds.includes(id));
    }

    handleReportSelection = (reportId, isChecked) => {
        if (!reportId) return;
        this.setState((prevState) => {
            if (isChecked) {
                if (prevState.selectedReportIds.includes(reportId)) {
                    return null;
                }
                return {
                    selectedReportIds: [...prevState.selectedReportIds, reportId],
                };
            }
            return {
                selectedReportIds: prevState.selectedReportIds.filter((id) => id !== reportId),
            };
        });
    }

    handleSelectAllReports = (isChecked) => {
        const selectableIds = this.getSelectableReportIds();
        this.setState((prevState) => ({
            selectedReportIds: isChecked
                ? Array.from(new Set([...prevState.selectedReportIds, ...selectableIds]))
                : prevState.selectedReportIds.filter((id) => !selectableIds.includes(id)),
        }));
    }

    handleBulkDeleteReports = () => {
        const selectedIds = this.state.selectedReportIds;
        if (!selectedIds.length) return;

        const title = `Are you sure you want to delete ${selectedIds.length} selected report${selectedIds.length > 1 ? 's' : ''}?`;
        swal({
            title,
            icon: "warning",
            buttons: ["No", "Yes"],
            dangerMode: true,
        }).then(async (willDelete) => {
            if (!willDelete) return;

            try {
                const deleteRequests = selectedIds.map((id) => axios.delete(`/api/custom-Reports/${id}`));
                const results = await Promise.allSettled(deleteRequests);
                const successCount = results.filter((result) => result.status === 'fulfilled').length;
                const failedCount = results.length - successCount;

                if (successCount > 0) {
                    toast.success(`${successCount} report${successCount > 1 ? 's' : ''} deleted successfully.`);
                }
                if (failedCount > 0) {
                    toast.error(`Failed to delete ${failedCount} report${failedCount > 1 ? 's' : ''}.`);
                }
            } catch (error) {
                apiError(error);
            } finally {
                this.setState({ selectedReportIds: [] });
                this.fetchAllReports();
            }
        });
    }

    fetchModules = async () => {
        try {
            const response = await axios.get('/api/custom-reports/modules');
            this.setState({
                modules: Array.isArray(response.data.modules) ? response.data.modules : [],
                modulesLoaded: true,
            });
        } catch (error) {
            apiError(error);
            this.setState({ modules: [], modulesLoaded: true });
        }
    }

    loadPermissibleActions = async() => {
        const response = await axios.post('/api/iam-user/permissions', {
            actions: ['report-write']
        });
        this.setState({ permissibleActionsIAM: response.data.permissibleObj });
    }

    handleFilterChange = (filterUpdates) => {
        this.setState({
            ...filterUpdates,
            activePage: 1
        }, () => this.fetchAllReports());
    }

    render() {
        return (
            <div className="content-wrap">
                <div className="breadcrumbbar">
                    <div className="flexRow row">
                        <div className="col-md-4">
                            <div className="page-head d-flex  justify-content-between">
                                <h4 className="page-title">Reports</h4>
                            </div>
                        </div>
                        <div className="col-md-8 pr-0">
                            <div className="d-flex justify-content-end ai-btn-container">
                                <Button
                                    variant="primary"
                                    className='btn-with-icon ai-btn-static mr-2'
                                    onClick={() => this.props.history.push("awareness-manager?isAIReport=true")}
                                >
                                    <PiSparkleBold className="mr-1" /> Create with AI
                                </Button>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        (!this.state.permissibleActionsIAM['report-write'])
                                            ? (<Tooltip id="top">You don't have permission to access</Tooltip>)
                                            : (<Tooltip id="top" className="d-none"></Tooltip>)
                                    }
                                >
                                    <Button 
                                        variant="primary" 
                                        className={`btn-with-icon ${!this.state.permissibleActionsIAM['report-write'] ? 'disabledCls2' : ''}`} 
                                        onClick={() => this.state.permissibleActionsIAM['report-write'] && this.setState({ showCreateReportModal: true })}
                                    >
                                        <PiPlus /> Create Report
                                    </Button>
                                </OverlayTrigger>
                            </div>
                        </div>
                    </div>
                </div>


                <div className="contentbar">
                    <div className="filter-without-border">
                        <div className="row align-items-center">
                            <div className="col-md-3">
                                <div className="positionSearchIcon">
                                    <InputGroup className="search-wrap">
                                        <FormControl 
                                            type="text" 
                                            placeholder="Search" 
                                            value={this.state.search} 
                                            onChange={(e) => this.setState({ search: e.target.value, activePage: 1 }, () => this.debouncedFetchAllReports())} 
                                            className="inputSearch mr-sm-2 form-control" 
                                        />
                                        <SearchSharpIcon className="search-icon"></SearchSharpIcon>
                                    </InputGroup>
                                </div>
                            </div>
                            <div className="col-md-9">
                                <div className="rightFilters">
                                    <Dropdown className="d-inline-block">
                                        <Dropdown.Toggle variant="secondary" id="reports-limit-dropdown" className="dropdown-color-white mr-2">
                                            Show{this.state.showLimitInLabel ? ` (${this.state.limit})` : ''}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {[15, 25, 50, 100].map((limit) => (
                                                <Dropdown.Item
                                                    key={`reports-limit-${limit}`}
                                                    onClick={() => this.setState({ limit, activePage: 1, showLimitInLabel: true }, () => this.fetchAllReports())}
                                                    active={this.state.limit === limit}
                                                >
                                                    {limit}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>

                                    <Dropdown className="d-inline-block">
                                        <Dropdown.Toggle variant="secondary" id="reports-type-dropdown" className="dropdown-color-white mr-2">
                                            {this.state.selectedType || 'Type'}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item
                                                onClick={() => this.handleFilterChange({ selectedType: '' })}
                                                active={!this.state.selectedType}
                                            >
                                                All
                                            </Dropdown.Item>
                                            {['Custom', 'Default'].map((type) => (
                                                <Dropdown.Item
                                                    key={`reports-type-${type}`}
                                                    onClick={() => this.handleFilterChange({ selectedType: type })}
                                                    active={this.state.selectedType === type}
                                                >
                                                    {type}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                        <Dropdown className="d-inline-block">
                                            <Dropdown.Toggle variant="secondary" id="reports-module-dropdown" className="dropdown-color-white">
                                                {this.state.selectedModule ? this.formatModuleLabel(this.state.selectedModule) : 'Category'}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item
                                                    onClick={() => this.handleFilterChange({ selectedModule: '' })}
                                                    active={!this.state.selectedModule}
                                                >
                                                    All
                                                </Dropdown.Item>
                                                {this.state.modules.map((module) => (
                                                    <Dropdown.Item
                                                        key={`reports-module-${module}`}
                                                        onClick={() => this.handleFilterChange({ selectedModule: module })}
                                                        active={this.state.selectedModule === module}
                                                    >
                                                        {this.formatModuleLabel(module)}
                                                    </Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                </div>
                            </div>
                        </div>
                    </div>



                {this.state.selectedReportIds.length > 0 && (
                    <div className="deleteUser" style={{ margin:0,marginBottom: "10px" }}>
                        <div className="selectedCount">
                            <p style={{ fontSize: "14px" }}>{this.state.selectedReportIds.length} selected</p>
                        </div>
                        <Button variant="danger" className="btn-sm btn-trans-danger" onClick={this.handleBulkDeleteReports}>
                            <PiTrash style={{ marginTop: "-3px" }} /> Delete
                        </Button>
                    </div>
                )}

                    <div className="tableContainerWrapInner reports-list-table-wrap" style={{ overflowX: 'auto' }}>
                        <Table>
                            <thead>
                                <tr>
                                    <th className="table-checkbox reports-list-checkbox-cell">
                                        <input
                                            type="checkbox"
                                            checked={this.areAllSelectableReportsSelected()}
                                            onChange={(e) => this.handleSelectAllReports(e.target.checked)}
                                            aria-label="Select all reports"
                                            className="checkbox"

                                        />
                                    </th>
                                    <th className="th-30">Report Name</th>
                                    <th>Category</th>
                                    <th>Total Records</th>
                                    <th>Created By</th>
                                    <th>Last Modified</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.reports.length > 0 ? this.state.reports.map((report, index) => (
                                    <tr key={report.id || report.reportName || index}>
                                        <td className="table-checkbox reports-list-checkbox-cell">
                                                <input
                                                    className="checkbox"
                                                    type="checkbox"
                                                    checked={this.state.selectedReportIds.includes(report.id)}
                                                    disabled={!this.state.permissibleActionsIAM['report-write'] || this.isDefaultReport(report)}
                                                    onChange={(e) => this.handleReportSelection(report.id, e.target.checked)}
                                                    aria-label={`Select ${report.name}`}
                                                />
                                        </td>
                                        <td className="th-30" onClick={() => this.handleReportOpen(report)}>
                                            <span className="lgTxt"> 
                                                <PiFileTextBold size={15} /> 
                                                <span className="report-name">{report.name}</span>
                                                {this.isDefaultReport(report) ? <Badge variant="light" className="default-report-badge">Default</Badge> : null}
                                            </span>
                                        </td>
                                        <td><Badge variant="primary" className="badge-soft-primary">{this.getReportModuleLabel(report)}</Badge></td>
                                        <td>{report.totalRecords || report.totalCount || 0}</td>
                                        <td>{report.createdBy}</td>
                                        <td>{getDateWithoutTimeInDdMmYyyy(report.updatedAt)}</td>
                                        <td>    
                                            <Dropdown className="action-dropdown dropdownIcon" ref={this.selectRef} drop="down">
                                                <Dropdown.Toggle className="btn-only" id={`dropdown-basic-${report.id}`}>
                                                    <MoreVertSharpIcon className="dot-icon" />
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item onClick={() => this.handleReportOpen(report)}>Preview</Dropdown.Item>
                                                    {this.state.permissibleActionsIAM['report-write'] && !this.isDefaultReport(report) ? 
                                                        <Dropdown.Item onClick={() => this.handleReportDelete(report.id)}>Delete</Dropdown.Item> : null}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </td>
                                    </tr>
                                )) : <tr><td colSpan="7">No reports found</td></tr>}
                            </tbody>
                        </Table>
                    </div>
                    
                    <div className="pagination-Wrap" style={{ border: '1px solid #dfe2e6', borderTop: 'none' }}>
                        <div className="row">
                            <div className="col-md-6">
                                {!this.state['loading'] ?
                                    <Pagination
                                        itemClass="page-item"
                                        linkClass="page-link"
                                        activePage={this.state.activePage}
                                        itemsCountPerPage={this.state.limit}
                                        totalItemsCount={this.state.count}
                                        onChange={(pageNumber) => this.setState({ activePage: pageNumber }, () => this.fetchAllReports())}
                                    /> : <Loader />
                                }
                            </div>
                            <div className="col-md-6 text-right d-flex align-items-center footer-pg">
                                <div className="w-100 pgCount">
                                    <p>{this.state.count === 0 ? 0 : this.state.activePage * this.state.limit + 1 - this.state.limit}-
                                        {this.state.activePage * this.state.limit > this.state.count ? this.state.count : this.state.activePage * this.state.limit} of {this.state.count} records</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {this.state.showCreateReportModal && (
                    <CreateReportModal
                        show={this.state.showCreateReportModal}
                        onHide={() => this.setState({ showCreateReportModal: false })}
                        onSubmit={(data) => {
                            this.setState({ showCreateReportModal: false });
                            this.fetchAllReports();
                        }}
                    />
                )}
            </div>
        );
    }
}
